import { type ReactNode } from "react";
import { cn } from "@/lib/cn.js";

export interface Column<T> {
  header: string;
  cell: (row: T) => ReactNode;
  className?: string;
}

/**
 * Generic table used by the admin pages. Optional `selectedRowKey` highlights
 * one row so j/k row-navigation shortcuts work alongside the `e` edit shortcut.
 *
 * `getRowKey` lets each page expose its row's stable identifier (typically `id`).
 * When `onRowClick` is supplied, rows become clickable.
 */
export function DataTable<T>({
  rows,
  columns,
  empty,
  selectedRowKey,
  getRowKey,
  onRowClick
}: {
  rows: T[];
  columns: Column<T>[];
  empty?: ReactNode;
  selectedRowKey?: string | null;
  getRowKey?: (row: T) => string;
  onRowClick?: (row: T) => void;
}): React.JSX.Element {
  if (rows.length === 0) {
    return (
      <div className="surface-card flex flex-col items-center gap-1 p-10 text-center">
        <div className="text-sm font-medium text-foreground">No rows yet</div>
        <div className="text-xs text-muted-foreground">{empty ?? "Get started by creating one."}</div>
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-lg border bg-card shadow-xs">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b bg-muted/40">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.header}
                  className={cn(
                    "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground",
                    c.className
                  )}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const key = getRowKey?.(row) ?? String(i);
              const isSelected = selectedRowKey != null && selectedRowKey === key;
              return (
                <tr
                  key={key}
                  data-row-key={key}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    "border-t transition-colors",
                    onRowClick && "cursor-pointer hover:bg-muted/40",
                    isSelected && "bg-primary/8 ring-1 ring-inset ring-primary/30 hover:bg-primary/10"
                  )}
                >
                  {columns.map((c) => (
                    <td key={c.header} className={cn("px-3 py-2.5 align-middle text-foreground", c.className)}>
                      {c.cell(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
